import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/contexts/LanguageContext';
import { Save, Eye, Link, Settings, ArrowLeft, ExternalLink, Phone, Mail, Globe, MessageCircle } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';

interface ButtonLink {
  id: string;
  name: string;
  currentUrl: string;
  description: string;
  category: string;
}

const AdminDashboard = () => {
  const { t } = useLanguage();
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [buttonLinks, setButtonLinks] = useState<ButtonLink[]>([
    // Hero Section Buttons
    {
      id: 'hero-primary-cta',
      name: 'Free Audit Button (Hero)',
      currentUrl: '#contact',
      description: 'Main CTA button in hero section - "تحليل مجاني" / "Free Audit"',
      category: 'Hero Section'
    },
    {
      id: 'hero-secondary-cta',
      name: 'View Our Work Button (Hero)',
      currentUrl: '#services',
      description: 'Secondary button in hero section - "شاهد أعمالنا" / "View Our Work"',
      category: 'Hero Section'
    },
    
    // Navigation Buttons
    {
      id: 'nav-cta',
      name: 'Navigation CTA Button',
      currentUrl: '#contact',
      description: 'CTA button in navigation bar (desktop)',
      category: 'Navigation'
    },
    {
      id: 'mobile-nav-cta',
      name: 'Mobile Menu CTA Button',
      currentUrl: '#contact',
      description: 'CTA button in mobile hamburger menu',
      category: 'Navigation'
    },
    {
      id: 'logo-link',
      name: 'Logo Click Action',
      currentUrl: '/',
      description: 'What happens when logo is clicked',
      category: 'Navigation'
    },
    
    // Contact Section
    {
      id: 'contact-form-submit',
      name: 'Contact Form Submit Action',
      currentUrl: 'mailto:info@reesha.agency',
      description: 'What happens when contact form is submitted',
      category: 'Contact'
    },
    {
      id: 'phone-link',
      name: 'Phone Number Link',
      currentUrl: 'tel:+966501234567',
      description: 'Phone number in contact section',
      category: 'Contact'
    },
    {
      id: 'email-link',
      name: 'Email Address Link',
      currentUrl: 'mailto:info@reesha.agency',
      description: 'Email address in contact section',
      category: 'Contact'
    },
    
    // External Links
    {
      id: 'external-portfolio',
      name: 'External Portfolio Link',
      currentUrl: 'https://portfolio.reesha.agency',
      description: 'Link to external portfolio/work showcase',
      category: 'External Links'
    },
    {
      id: 'whatsapp-contact',
      name: 'WhatsApp Contact',
      currentUrl: 'https://wa.me/966501234567',
      description: 'WhatsApp contact link',
      category: 'External Links'
    },
    {
      id: 'linkedin-profile',
      name: 'LinkedIn Profile',
      currentUrl: 'https://linkedin.com/company/reesha-agency',
      description: 'Company LinkedIn profile',
      category: 'Social Media'
    },
    {
      id: 'instagram-profile',
      name: 'Instagram Profile',
      currentUrl: 'https://instagram.com/reesha.agency',
      description: 'Company Instagram profile',
      category: 'Social Media'
    },
    {
      id: 'twitter-profile',
      name: 'Twitter/X Profile',
      currentUrl: 'https://twitter.com/reesha_agency',
      description: 'Company Twitter/X profile',
      category: 'Social Media'
    },
    {
      id: 'youtube-channel',
      name: 'YouTube Channel',
      currentUrl: 'https://youtube.com/@reesha-agency',
      description: 'Company YouTube channel',
      category: 'Social Media'
    }
  ]);

  // Load saved links from localStorage
  useEffect(() => {
    const savedLinks = localStorage.getItem('admin-button-links');
    if (savedLinks) {
      setButtonLinks(JSON.parse(savedLinks));
    }
  }, []);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === 'GrowthPro2024!') {
      setIsAuthenticated(true);
    } else {
      alert('Incorrect password');
    }
  };

  const handleLinkUpdate = (id: string, newUrl: string) => {
    const updatedLinks = buttonLinks.map(link => 
      link.id === id ? { ...link, currentUrl: newUrl } : link
    );
    setButtonLinks(updatedLinks);
  };

  const handleSaveAll = () => {
    localStorage.setItem('admin-button-links', JSON.stringify(buttonLinks));
    
    // Update the actual website buttons
    updateWebsiteButtons();
    
    alert('All button links have been updated successfully! The page will reload to apply changes.');
  };

  const updateWebsiteButtons = () => {
    // Save all button URLs to localStorage with their specific keys
    buttonLinks.forEach(link => {
      localStorage.setItem(`${link.id}-url`, link.currentUrl);
    });

    // Trigger a page reload to apply changes
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  const previewLink = (url: string) => {
    if (url.startsWith('#')) {
      alert(`This is an anchor link that will scroll to section: ${url}`);
    } else if (url.startsWith('mailto:')) {
      alert(`This will open email client to: ${url.replace('mailto:', '')}`);
    } else if (url.startsWith('tel:')) {
      alert(`This will initiate a phone call to: ${url.replace('tel:', '')}`);
    } else if (url.startsWith('https://wa.me/')) {
      alert(`This will open WhatsApp to: ${url.replace('https://wa.me/', '')}`);
    } else {
      window.open(url, '_blank');
    }
  };

  const getQuickActions = (category: string) => {
    const commonActions = [
      { label: 'Contact Section', value: '#contact' },
      { label: 'Services Section', value: '#services' },
      { label: 'Process Section', value: '#process' },
      { label: 'Home Section', value: '#home' }
    ];

    const contactActions = [
      { label: 'Email Contact', value: 'mailto:info@reesha.agency' },
      { label: 'Phone Contact', value: 'tel:+966501234567' },
      { label: 'WhatsApp', value: 'https://wa.me/966501234567' }
    ];

    const externalActions = [
      { label: 'Portfolio Site', value: 'https://portfolio.reesha.agency' },
      { label: 'LinkedIn', value: 'https://linkedin.com/company/reesha-agency' },
      { label: 'Instagram', value: 'https://instagram.com/reesha.agency' }
    ];

    if (category === 'Contact') {
      return [...commonActions, ...contactActions];
    } else if (category === 'External Links' || category === 'Social Media') {
      return [...commonActions, ...externalActions];
    }
    return commonActions;
  };

  const groupedLinks = buttonLinks.reduce((groups, link) => {
    const category = link.category;
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(link);
    return groups;
  }, {} as Record<string, ButtonLink[]>);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold">Admin Dashboard</CardTitle>
            <p className="text-muted-foreground">Enter password to manage button links</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  required
                />
              </div>
              <Button type="submit" className="w-full">
                Login
              </Button>
            </form>
            <div className="mt-4 p-3 bg-muted/50 rounded text-sm text-muted-foreground">
              <p><strong>Default Password:</strong> GrowthPro2024!</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <RouterLink to="/">
              <Button variant="outline" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Website
              </Button>
            </RouterLink>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Button Links Manager</h1>
              <p className="text-muted-foreground">Manage all button URLs and actions on your website</p>
            </div>
          </div>
          <Button onClick={handleSaveAll} className="bg-green-600 hover:bg-green-700">
            <Save className="w-4 h-4 mr-2" />
            Save All Changes
          </Button>
        </div>

        {/* Instructions */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Instructions & Link Types
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-muted-foreground">
              <div>
                <h4 className="font-semibold text-foreground mb-2">Internal Links:</h4>
                <p><strong>Anchor Links:</strong> #section-name (e.g., #contact, #services)</p>
                <p><strong>Home Page:</strong> / or #home</p>
              </div>
              <div>
                <h4 className="font-semibold text-foreground mb-2">External Links:</h4>
                <p><strong>Websites:</strong> https://example.com</p>
                <p><strong>Email:</strong> mailto:email@domain.com</p>
                <p><strong>Phone:</strong> tel:+1234567890</p>
                <p><strong>WhatsApp:</strong> https://wa.me/1234567890</p>
              </div>
            </div>
            <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded border border-yellow-200 dark:border-yellow-800">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>Note:</strong> Changes will be applied after clicking "Save All Changes" and the page will reload automatically.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Button Links Management by Category */}
        <div className="space-y-8">
          {Object.entries(groupedLinks).map(([category, links]) => (
            <div key={category}>
              <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                {category === 'Hero Section' && <Globe className="w-6 h-6" />}
                {category === 'Navigation' && <Link className="w-6 h-6" />}
                {category === 'Contact' && <Mail className="w-6 h-6" />}
                {category === 'External Links' && <ExternalLink className="w-6 h-6" />}
                {category === 'Social Media' && <MessageCircle className="w-6 h-6" />}
                {category}
              </h2>
              
              <div className="grid gap-4">
                {links.map((link) => (
                  <Card key={link.id}>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <Link className="w-5 h-5" />
                          {link.name}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => previewLink(link.currentUrl)}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Preview
                        </Button>
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">{link.description}</p>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <Label htmlFor={`url-${link.id}`}>Button URL/Action</Label>
                          <Input
                            id={`url-${link.id}`}
                            value={link.currentUrl}
                            onChange={(e) => handleLinkUpdate(link.id, e.target.value)}
                            placeholder="Enter URL, anchor link, or action"
                            className="font-mono"
                          />
                        </div>
                        
                        {/* Quick Actions */}
                        <div className="flex flex-wrap gap-2">
                          {getQuickActions(category).map((action) => (
                            <Button
                              key={action.value}
                              variant="outline"
                              size="sm"
                              onClick={() => handleLinkUpdate(link.id, action.value)}
                            >
                              {action.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Current Status Summary */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle>Current Button Configuration Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm">
              {Object.entries(groupedLinks).map(([category, links]) => (
                <div key={category}>
                  <h4 className="font-semibold text-foreground mb-2">{category}:</h4>
                  <div className="ml-4 space-y-1">
                    {links.map((link) => (
                      <div key={link.id} className="flex justify-between items-center p-2 bg-muted/30 rounded">
                        <span className="font-medium">{link.name}:</span>
                        <code className="text-xs bg-background px-2 py-1 rounded max-w-xs truncate">
                          {link.currentUrl}
                        </code>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;